<script setup lang="ts">
import type { AgentHeroSectionProps } from '.';

import { useAgentProfileNavigation } from '~/composables/useAgentProfileNavigation';
import { usePropertyHeroShare } from '~/composables/usePropertyHeroShare';
import { MixpanelProps } from '~/core/service/mixpanel';
import { useAgentProfileStore } from '~/store/agentProfileStore';
import { buildAgentProfileShareUrl } from '~/utils/agentProfileShareUrl';
import { storeToRefs } from 'pinia';

import { AgentProfileHero } from '~/components/AgentProfileHero';
import { BrokerHighlights } from '~/components/BrokerHighlights';
import { GlassButton } from '~/components/GlassButton';
import { ShareContent } from '~/components/ShareContent';

const props = withDefaults(defineProps<AgentHeroSectionProps>(), {
	isLoading: false
});

const agentProfileStore = useAgentProfileStore();
const { profile, company, heroHighlights } = storeToRefs(agentProfileStore);

const { ORIGIN } = useRuntimeConfig().public;

// URL crua de propósito: quem resolve tamanho é o `NuxtImg` do hero, a partir
// do `sizes`, para servir uma variante por breakpoint em vez de uma largura só.
const profilePictureUrl = computed<string>(
	() =>
		profile.value?.profilePicture.croppedUrl ||
		profile.value?.profilePicture.url ||
		''
);

const { handleBackButtonClick } = useAgentProfileNavigation({
	profileId: () => profile.value?.id
});

// O evento continua sendo `share_property_clicked` — não existe evento próprio
// de corretor ainda. O `section_name` é o que permite separar este share do de
// anúncio na análise, e precisa ir pelos dois caminhos: o composable cobre as
// redes e o share nativo, a prop do `ShareContent` cobre o copiar link.
const shareAnalyticsExtras = {
	[MixpanelProps.SECTION_NAME]: 'agent profile hero'
} as const;

const {
	showShareContent,
	shareContentRef,
	shareContentButton,
	shareUrl,
	toggleShareContent,
	shareMobile,
	handleShareClicked
} = usePropertyHeroShare({
	property: computed(() => null),
	shareUrlOverride: () =>
		buildAgentProfileShareUrl(ORIGIN, profile.value?.slug),
	shareTitleOverride: () =>
		profile.value?.name
			? `${profile.value.name} | PilarHomes`
			: 'Perfil do corretor | PilarHomes',
	getShareTrackExtras: () => shareAnalyticsExtras
});
</script>

<template>
	<AgentProfileHero
		:class="props.class"
		:is-loading="isLoading"
		:name="profile?.name"
		:profile-picture-url="profilePictureUrl"
		:company-name="company?.name"
		:company-logo-url="company?.smallLogo"
		@back-click="handleBackButtonClick"
	>
		<template v-if="heroHighlights.length" #stats>
			<BrokerHighlights :items="heroHighlights" variant="dark" layout="fluid" />
		</template>

		<template #share>
			<GlassButton
				size="sm"
				:disabled="isLoading"
				class="lg:hidden"
				aria-label="Compartilhar perfil"
				@click="shareMobile"
			>
				<IconPilarSend48Degrees class="size-5 text-white" />
			</GlassButton>

			<div class="relative hidden lg:block">
				<GlassButton
					ref="shareContentButton"
					:disabled="isLoading"
					size="md"
					aria-label="Compartilhar perfil"
					@click="toggleShareContent"
				>
					<IconPilarSend48Degrees class="size-5 text-white" />
				</GlassButton>

				<div
					v-show="showShareContent"
					ref="shareContentRef"
					class="absolute right-0 top-full z-40 mt-2 w-[180px] border border-white-dark-3 bg-white p-[0.7rem_1rem]"
				>
					<ShareContent
						v-model="showShareContent"
						variant="dropdown"
						mail-subject="Conheça este corretor na PilarHomes"
						:content-to-share="shareUrl"
						:share-analytics-extras="shareAnalyticsExtras"
						show-labels
						@share-clicked="handleShareClicked"
					/>
				</div>
			</div>
		</template>
	</AgentProfileHero>
</template>
